import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useDebounce } from './useDebounce';
import { clearError, clearPatients } from '@/store/slices/patients';
import { fetchPatients, fetchWeekendDischargePatients, fetchUnits } from '@/store/slices/patients/patients-api';
import type { UnitOption } from '@/types/patient';
import { isSilverStayUser, isSupervisor } from '@/utils/role';
import { fetchIngestFilesLastSyncDate } from '@/store/slices/ingest-files';
import { DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '@/constant';
import { registerSessionCacheInvalidator } from '@/services/auth-session';

interface UsePatientsOptions {
    autoFetch?: boolean;
    /** Dashboard list — `GET /patient-handoffs`. Omit `type` for All; `weekend` | `general` otherwise. */
    useWeekendDischargeEndpoint?: boolean;
    unitRangeId?: string;
    /** Applied when no `unit` query param is present — e.g. `ALL_UNITS_FILTER` for admins. */
    defaultUnitFilter?: string | null;
    /** Applied when the URL has no handoff type. `''` is All. */
    defaultHandOffType?: '' | 'weekend' | 'general';
    fetchingIngestFilesLastSyncDateAllowed?: boolean;
}

interface SortConfig {
    key: string | null;
    direction: 'asc' | 'desc';
}

interface FilterConfig {
    gender: string | null;
    unit: string | null;
    tier: string | null;
    readmitRisk: string | null;
    dcToday: string | null;
    /** `''` / omitted = all; `weekend` | `general`. */
    handOffType?: '' | 'weekend' | 'general';
}

export const HANDOFF_TYPE_FILTER_ALL = '';
export const HANDOFF_TYPE_FILTER_WEEKEND = 'weekend' as const;
export const HANDOFF_TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'weekend', label: 'Weekend' },
    { value: 'general', label: 'General' }
];
/** Handoff page type filter. All omits the listing `type` param. */
export const HANDOFF_TYPE_ENDPOINT_FILTER_OPTIONS = HANDOFF_TYPE_FILTER_OPTIONS;

export const getHandoffTypeFilterOptions = (readonlyWeekendDischargeColumn = false) =>
    readonlyWeekendDischargeColumn
        ? HANDOFF_TYPE_FILTER_OPTIONS.filter(({ value }) => value !== HANDOFF_TYPE_FILTER_OPTIONS[2].value)
        : HANDOFF_TYPE_FILTER_OPTIONS;

const normalizeHandOffTypeFilter = (value: string | null): '' | 'weekend' | 'general' => {
    if (value === 'weekend' || value === 'general') {
        return value;
    }
    return HANDOFF_TYPE_FILTER_ALL;
};

/** Maps UI select value (`all` | `weekend` | `general`) to filter/API value. */
export const toHandOffTypeFilterValue = (selectValue: string): '' | 'weekend' | 'general' =>
    selectValue === 'weekend' || selectValue === 'general' ? selectValue : HANDOFF_TYPE_FILTER_ALL;

/** Maps filter/API value to FormControlSelect option value (empty cannot be selected). */
export const toHandOffTypeSelectValue = (handOffType: '' | 'all' | 'weekend' | 'general' | null | undefined): string =>
    handOffType === 'weekend' || handOffType === 'general' ? handOffType : 'all';

let lastLoadedPatientsQueryKey: string | null = null;
let lastLoadedUnitsHospitalId: string | null = null;

/** Clears in-memory reuse keys so the next patients listing mount refetches. */
export const invalidatePatientsCache = () => {
    lastLoadedPatientsQueryKey = null;
    lastLoadedUnitsHospitalId = null;
};

registerSessionCacheInvalidator(invalidatePatientsCache);

export const ASSIGNED_UNITS_FILTER = 'assigned-units';
export const ALL_UNITS_FILTER = 'all';

const normalizeUnitFilter = (unit: string | null): string | null => {
    if (!unit) {
        return null;
    }
    return unit;
};

export const resolveUnitFilterSelection = (unit: string | null, defaultUnitFilter: string | null = null): string => {
    if (unit === ALL_UNITS_FILTER) {
        return ALL_UNITS_FILTER;
    }
    if (unit === ASSIGNED_UNITS_FILTER) {
        return ASSIGNED_UNITS_FILTER;
    }
    if (unit) {
        return unit;
    }
    if (defaultUnitFilter === ALL_UNITS_FILTER) {
        return ALL_UNITS_FILTER;
    }
    return ASSIGNED_UNITS_FILTER;
};

/** Maps UI unit filter to the `unit` query param for patient/census list APIs. */
export const toApiUnitParam = (unitFilter: string | null | undefined, defaultUnitFilter: string | null = null): string | undefined => {
    const trimmed = unitFilter?.trim() ?? '';

    if (trimmed === ALL_UNITS_FILTER) {
        return ALL_UNITS_FILTER;
    }

    // Explicit Assigned Units — send `assigned-units` so the API scopes to the caller's assigned units.
    // Must not fall through to the defaultUnitFilter fallback (supervisors default to All Units).
    if (trimmed === ASSIGNED_UNITS_FILTER) {
        return ASSIGNED_UNITS_FILTER;
    }

    if (trimmed) {
        return trimmed;
    }

    if (defaultUnitFilter === ALL_UNITS_FILTER) {
        return ALL_UNITS_FILTER;
    }

    return undefined;
};

const normalizeRowsPerPage = (limit: number): number =>
    (ROWS_PER_PAGE_OPTIONS as readonly number[]).includes(limit) ? limit : DEFAULT_ROWS_PER_PAGE;

export const usePatients = ({
    autoFetch = true,
    useWeekendDischargeEndpoint = false,
    unitRangeId,
    defaultUnitFilter = null,
    defaultHandOffType,
    fetchingIngestFilesLastSyncDateAllowed = false
}: UsePatientsOptions = {}) => {
    const dispatch = useAppDispatch();
    const { hospitalId: hospitalIdFromRoute } = useParams<{ hospitalId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsString = searchParams.toString();

    const { patients, units, total, serverPage, serverLimit, loading, unitsLoading, error, unitsError } = useAppSelector(
        (state) => state.patients
    );
    const { currentHospitalId } = useAppSelector(({ hospital: hospitalState }) => hospitalState);
    const { profile } = useAppSelector(({ profile: profileState }) => profileState);
    const isSilverStay = isSilverStayUser(profile);
    const hospitalKey = hospitalIdFromRoute ?? currentHospitalId ?? null;
    const initialQueryState = useMemo(() => {
        const params = new URLSearchParams(searchParamsString);
        const initialPage = Number(params.get('page') ?? '1');
        const initialLimit = Number(params.get('limit') ?? String(DEFAULT_ROWS_PER_PAGE));
        const initialSortOrder = params.get('sortOrder');
        const rawHandOffType = params.get('handOffType');

        return {
            search: params.get('search') ?? '',
            unit: normalizeUnitFilter(params.get('unit')),
            handOffType: normalizeHandOffTypeFilter(rawHandOffType),
            hasExplicitHandOffTypeAll: rawHandOffType === 'all',
            page: Number.isFinite(initialPage) && initialPage > 0 ? initialPage - 1 : 0,
            limit: normalizeRowsPerPage(Number.isFinite(initialLimit) && initialLimit > 0 ? initialLimit : DEFAULT_ROWS_PER_PAGE),
            sortBy: params.get('sortBy'),
            sortDirection: initialSortOrder === 'DESC' ? ('desc' as const) : ('asc' as const)
        };
    }, [searchParamsString]);

    const [ingestFilesLastSyncDate, setIngestFilesLastSyncDate] = useState<string>('');

    // Search state
    const [inputValue, setInputValue] = useState(initialQueryState.search);
    const debouncedSearch = useDebounce(inputValue, 300);

    // Sort state
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: initialQueryState.sortBy ?? null,
        direction: initialQueryState.sortDirection
    });

    const resolvedDefaultUnitFilter = defaultUnitFilter ?? (isSilverStay || isSupervisor(profile) ? ALL_UNITS_FILTER : null);
    const resolvedDefaultHandOffType =
        defaultHandOffType ?? (useWeekendDischargeEndpoint ? HANDOFF_TYPE_FILTER_WEEKEND : HANDOFF_TYPE_FILTER_ALL);
    const hasUrlUnitFilter = Boolean(initialQueryState.unit);
    const hasUrlHandOffTypeFilter = Boolean(initialQueryState.handOffType) || initialQueryState.hasExplicitHandOffTypeAll;

    // Filter state
    const [filterConfig, setFilterConfig] = useState<FilterConfig>({
        gender: null,
        unit: initialQueryState.unit ?? resolvedDefaultUnitFilter,
        tier: null,
        readmitRisk: null,
        dcToday: null,
        handOffType: hasUrlHandOffTypeFilter ? initialQueryState.handOffType : resolvedDefaultHandOffType
    });

    // Pagination state
    const [page, setPage] = useState(initialQueryState.page);
    const [rowsPerPage, setRowsPerPage] = useState(initialQueryState.limit);
    const activePatientsRequestRef = useRef<{ abort?: () => void } | null>(null);
    const [patientsFetchVersion, setPatientsFetchVersion] = useState(0);
    const hasUserChangedRowsPerPageRef = useRef(false);
    const hasUserChangedUnitFilterRef = useRef(hasUrlUnitFilter);
    const previousHospitalIdRef = useRef(hospitalKey);
    const isFirstSearchSyncRef = useRef(true);

    const loadUnits = useCallback(() => {
        dispatch(fetchUnits({ hospitalId: hospitalKey ?? undefined }));
    }, [dispatch, hospitalKey]);

    const selectedUnit = useMemo(
        () => toApiUnitParam(filterConfig.unit, resolvedDefaultUnitFilter),
        [filterConfig.unit, resolvedDefaultUnitFilter]
    );
    const patientsRequestParams = useMemo(
        () => ({
            search: debouncedSearch.trim() || undefined,
            unit: selectedUnit,
            unitRangeId: unitRangeId || undefined,
            page: page + 1,
            limit: rowsPerPage,
            sortBy: sortConfig.key || undefined,
            sortOrder: sortConfig.key ? (sortConfig.direction === 'asc' ? ('ASC' as const) : ('DESC' as const)) : undefined,
            handOffType: filterConfig.handOffType || undefined
        }),
        [
            debouncedSearch,
            selectedUnit,
            unitRangeId,
            page,
            rowsPerPage,
            sortConfig.key,
            sortConfig.direction,
            filterConfig.handOffType,
            useWeekendDischargeEndpoint
        ]
    );
    const patientsQueryKey = useMemo(
        () =>
            JSON.stringify({
                hospitalId: hospitalKey,
                endpoint: useWeekendDischargeEndpoint ? 'patient-handoffs' : 'patients',
                ...patientsRequestParams
            }),
        [hospitalKey, patientsRequestParams, useWeekendDischargeEndpoint]
    );

    // Fetch patients
    const loadPatients = useCallback(() => {
        activePatientsRequestRef.current?.abort?.();

        const requestPayload = { ...patientsRequestParams, hospitalId: hospitalKey ?? undefined };
        const patientsRequest = dispatch(
            useWeekendDischargeEndpoint ? fetchWeekendDischargePatients(requestPayload) : fetchPatients(requestPayload)
        );
        lastLoadedPatientsQueryKey = patientsQueryKey;
        activePatientsRequestRef.current = patientsRequest;
        return patientsRequest;
    }, [dispatch, patientsRequestParams, patientsQueryKey, hospitalKey, useWeekendDischargeEndpoint]);

    // Clear patients
    const clearAllPatients = useCallback(() => {
        lastLoadedPatientsQueryKey = null;
        setPatientsFetchVersion((version) => version + 1);
        dispatch(clearPatients());
    }, [dispatch]);

    // Clear error
    const clearPatientError = useCallback(() => {
        dispatch(clearError());
    }, [dispatch]);

    // Handle sort
    const handleSort = useCallback((key: string) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, []);

    // Handle filter
    const handleFilter = useCallback((key: keyof FilterConfig, value: string | null) => {
        let didChange = false;
        if (key === 'unit') {
            hasUserChangedUnitFilterRef.current = true;
        }
        const nextValue = key === 'handOffType' ? normalizeHandOffTypeFilter(value) : value;
        setFilterConfig((prev) => {
            if (prev[key] === nextValue) {
                return prev;
            }
            didChange = true;
            return {
                ...prev,
                [key]: nextValue
            };
        });
        if (didChange) {
            setPage((prev) => (prev === 0 ? prev : 0));
        }
    }, []);

    // Pagination handlers
    const handleChangePage = useCallback((newPage: number) => {
        setPage((prev) => (prev === newPage ? prev : newPage));
    }, []);

    const handleChangeRowsPerPage = useCallback((newRowsPerPage: number) => {
        hasUserChangedRowsPerPageRef.current = true;
        setRowsPerPage((prev) => (prev === newRowsPerPage ? prev : newRowsPerPage));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, []);

    // Clear all filters
    const clearFilters = useCallback(() => {
        hasUserChangedUnitFilterRef.current = false;
        setFilterConfig((prev) => {
            if (
                !prev.gender &&
                prev.unit === resolvedDefaultUnitFilter &&
                !prev.tier &&
                !prev.readmitRisk &&
                !prev.dcToday &&
                prev.handOffType === resolvedDefaultHandOffType
            ) {
                return prev;
            }
            return {
                gender: null,
                unit: resolvedDefaultUnitFilter,
                tier: null,
                readmitRisk: null,
                dcToday: null,
                handOffType: resolvedDefaultHandOffType
            };
        });
        setInputValue((prev) => (prev === '' ? prev : ''));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, [resolvedDefaultHandOffType, resolvedDefaultUnitFilter]);

    // Filtered patients (local-only filters until backend supports them)
    const processedPatients = useMemo(() => {
        let result = [...patients];

        // Apply filters not yet supported by API
        if (filterConfig.gender) {
            result = result.filter((p) => p.gender === filterConfig.gender);
        }
        if (filterConfig.readmitRisk) {
            result = result.filter((p) => p.readmitRisk === filterConfig.readmitRisk);
        }
        if (filterConfig.dcToday) {
            result = result.filter((p) => p.dcToday === filterConfig.dcToday);
        }

        return result;
    }, [patients, filterConfig]);

    const hasClientOnlyFilters = Boolean(filterConfig.gender || filterConfig.readmitRisk || filterConfig.dcToday);
    const effectiveTotalCount = hasClientOnlyFilters ? processedPatients.length : total;

    // Get unique filter options
    const filterOptions = useMemo<{ units: string[] }>(() => {
        const mappedUnitOptions: string[] = units
            .map((unit: UnitOption) => unit.unitCode || unit.unitName)
            .filter((unit): unit is string => typeof unit === 'string' && unit.trim().length > 0);
        const unitsFromApi = [...new Set(mappedUnitOptions)].sort((a, b) => a.localeCompare(b));

        return {
            units: unitsFromApi
        };
    }, [units]);

    const unitOptions = useMemo<{ value: string; label: string }[]>(
        () => [
            ...(isSilverStay ? [] : [{ value: ASSIGNED_UNITS_FILTER, label: 'Assigned Units' }]),
            { value: ALL_UNITS_FILTER, label: 'All Units' },
            ...filterOptions.units.map((unit) => ({ value: unit, label: unit }))
        ],
        [filterOptions.units, isSilverStay]
    );

    const selectedUnitFilter = useMemo(() => {
        const resolved = resolveUnitFilterSelection(filterConfig.unit, resolvedDefaultUnitFilter);
        if (isSilverStay && resolved === ASSIGNED_UNITS_FILTER) {
            return ALL_UNITS_FILTER;
        }
        return resolved;
    }, [filterConfig.unit, resolvedDefaultUnitFilter, isSilverStay]);

    // Auto fetch on mount
    useEffect(() => {
        const shouldReuseUnits = units.length > 0 && lastLoadedUnitsHospitalId === hospitalKey;
        if (autoFetch && !shouldReuseUnits) {
            lastLoadedUnitsHospitalId = hospitalKey;
            loadUnits();
        }
    }, [autoFetch, loadUnits, units.length, hospitalKey]);

    useEffect(() => {
        if (!hasUserChangedRowsPerPageRef.current && serverLimit && serverLimit !== rowsPerPage) {
            setRowsPerPage(normalizeRowsPerPage(serverLimit));
        }
    }, [serverLimit, rowsPerPage]);

    useEffect(() => {
        // An empty result is a completed load. Refetch only when the query changes or the list is cleared.
        if (!autoFetch || lastLoadedPatientsQueryKey === patientsQueryKey) {
            return;
        }
        loadPatients();
    }, [autoFetch, loadPatients, patientsFetchVersion, patientsQueryKey]);

    useEffect(() => {
        if (previousHospitalIdRef.current === hospitalKey) {
            return;
        }

        previousHospitalIdRef.current = hospitalKey;
        lastLoadedPatientsQueryKey = null;
        lastLoadedUnitsHospitalId = null;
        hasUserChangedUnitFilterRef.current = false;
        setFilterConfig((prev) => {
            if (prev.unit === resolvedDefaultUnitFilter && prev.handOffType === resolvedDefaultHandOffType) {
                return prev;
            }
            return {
                ...prev,
                unit: resolvedDefaultUnitFilter,
                handOffType: resolvedDefaultHandOffType
            };
        });
        setInputValue((prev) => (prev === '' ? prev : ''));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, [hospitalKey, resolvedDefaultHandOffType, resolvedDefaultUnitFilter]);

    useEffect(() => {
        if (hasUserChangedUnitFilterRef.current || hasUrlUnitFilter) {
            return;
        }

        setFilterConfig((prev) => (prev.unit === resolvedDefaultUnitFilter ? prev : { ...prev, unit: resolvedDefaultUnitFilter }));
    }, [hasUrlUnitFilter, resolvedDefaultUnitFilter]);

    useEffect(() => {
        if (isFirstSearchSyncRef.current) {
            isFirstSearchSyncRef.current = false;
            return;
        }
        setPage((prev) => (prev === 0 ? prev : 0));
    }, [debouncedSearch]);

    useEffect(() => {
        const nextParams = new URLSearchParams(searchParamsString);
        if (debouncedSearch.trim()) {
            nextParams.set('search', debouncedSearch.trim());
        } else {
            nextParams.delete('search');
        }
        if (filterConfig.unit) {
            nextParams.set('unit', filterConfig.unit);
        } else {
            nextParams.delete('unit');
        }
        if (filterConfig.handOffType) {
            nextParams.set('handOffType', filterConfig.handOffType);
        } else if (useWeekendDischargeEndpoint) {
            // Empty filter is All. Persist it so a refresh does not fall back to the weekend default.
            nextParams.set('handOffType', 'all');
        } else {
            nextParams.delete('handOffType');
        }
        nextParams.set('page', String(page + 1));
        nextParams.set('limit', String(rowsPerPage));

        if (sortConfig.key) {
            nextParams.set('sortBy', String(sortConfig.key));
            nextParams.set('sortOrder', sortConfig.direction === 'asc' ? 'ASC' : 'DESC');
        } else {
            nextParams.delete('sortBy');
            nextParams.delete('sortOrder');
        }

        const nextParamsString = nextParams.toString();
        if (nextParamsString !== searchParamsString) {
            setSearchParams(nextParams, { replace: true });
        }
    }, [
        debouncedSearch,
        filterConfig.unit,
        filterConfig.handOffType,
        page,
        rowsPerPage,
        searchParamsString,
        setSearchParams,
        sortConfig.direction,
        sortConfig.key,
        useWeekendDischargeEndpoint
    ]);
    useEffect(() => {
        if (hospitalKey && fetchingIngestFilesLastSyncDateAllowed) {
            dispatch(fetchIngestFilesLastSyncDate({ hospitalId: hospitalKey }))
                .unwrap()
                .then((response) => {
                    setIngestFilesLastSyncDate(response.lastSyncDate);
                });
            return;
        }
        setIngestFilesLastSyncDate('');
    }, [dispatch, hospitalKey, fetchingIngestFilesLastSyncDateAllowed]);

    useEffect(
        () => () => {
            activePatientsRequestRef.current?.abort?.();
        },
        []
    );

    return {
        // Data
        patients: processedPatients,
        allPatients: patients,
        filteredCount: effectiveTotalCount,
        serverPage,
        serverLimit,
        loading,
        unitsLoading,
        error,
        unitsError,
        ingestFilesLastSyncDate,

        // Search
        inputValue,
        setInputValue,

        // Sort
        sortConfig,
        handleSort,

        // Filter
        filterConfig,
        filterOptions,
        unitOptions,
        selectedUnitFilter,
        defaultUnitFilter: resolvedDefaultUnitFilter,
        handleFilter,
        clearFilters,

        // Pagination
        page,
        rowsPerPage,
        handleChangePage,
        handleChangeRowsPerPage,

        // Actions
        loadPatients,
        loadUnits,
        clearAllPatients,
        clearPatientError
    };
};

export type { SortConfig, FilterConfig };
